import { ErrorMessage } from "@/components/common/Error.tsx";
import { cardListClass } from "@/components/threads/shared.tsx";
import { getConnectors } from "@/components/connectors/api.ts";
import {
  type ContactListCategory,
  DEFAULT_CONTACT_LIST_CATEGORY,
  matchesCategory,
} from "@/components/contacts/ContactFilters.tsx";
import { ContactItem } from "@/components/contacts/ContactItem.tsx";
import { MergeContactDialog } from "@/components/contacts/MergeContactDialog.tsx";
import { Loader } from "@/components/loader/Loader.tsx";
import { Button } from "@/components/ui/button.tsx";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog.tsx";
import { contactQueries, useContact, useContacts, useContactSearch } from "@/hooks/useContacts.tsx";
import { type Contact, type ContactListSort, DEFAULT_CONTACT_LIST_SORT } from "@/types/contact.ts";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/http.ts";
import { useRightSidebar } from "@/components/right-sidebar/hooks.tsx";
import type { ConnectorSummary } from "@/types/connectors.ts";
import { Loader2, Merge, Trash2 } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useLocation } from "wouter";
import { useMediaQuery } from "@/hooks/useMediaQuery.tsx";

const ContactRow = ({
  contact,
  connector,
  onOpen,
  onMerge,
  onDelete,
  isDeletePending,
}: {
  contact: Contact;
  connector?: ConnectorSummary;
  onOpen: () => void;
  onMerge: (contact: Contact, e: React.MouseEvent) => void;
  onDelete: (contact: Contact, e: React.MouseEvent) => void;
  isDeletePending: boolean;
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const canModify = contact.type !== "user" && contact.type !== "assistant";

  return (
    <div
      className="cursor-pointer"
      onClick={onOpen}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <ContactItem
        contact={contact}
        connector={connector}
        isHovered={isHovered}
        interactive
        actions={
          canModify ? (
            <div className="flex gap-1">
              <Button
                variant="ghost"
                size="icon"
                onClick={(e) => onMerge(contact, e)}
                title="Merge contact"
                data-statsig-name="contacts-merge"
              >
                <Merge className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={(e) => onDelete(contact, e)}
                disabled={isDeletePending}
                title="Delete contact"
                data-statsig-name="contacts-delete"
              >
                <Trash2 className="h-4 w-4 text-destructive" />
              </Button>
            </div>
          ) : undefined
        }
      />
    </div>
  );
};

/** The contacts list's full filter state, kept together so callers sync one value. */
export type ContactListFilters = {
  connectorId?: number;
  category: ContactListCategory;
  searchQuery: string;
  sort: ContactListSort;
};

export const DEFAULT_CONTACT_LIST_FILTERS: ContactListFilters = {
  category: DEFAULT_CONTACT_LIST_CATEGORY,
  searchQuery: "",
  sort: DEFAULT_CONTACT_LIST_SORT,
};

/**
 * Renders a single contact when given `contactId`, and otherwise every contact
 * with pagination.
 *
 * The list only reads `filters` — the controls that change them live in the
 * settings panel's header (search, category/sort/account pickers), so the main
 * contacts view passes URL-synced filters down and the sidebar omits them.
 */
export const ContactList = ({
  contactId,
  filters = DEFAULT_CONTACT_LIST_FILTERS,
  hasProfile = false,
  onOpenContact,
}: {
  contactId?: string;
  filters?: ContactListFilters;
  /** People view: list only contacts that have a contact profile (hides sections). */
  hasProfile?: boolean;
  /**
   * Host-supplied open behavior. The settings panel drills down to its own
   * child screen; every other host (the sidebar, the People page) omits this
   * and keeps the sidebar/stacked-page behavior below. The list can't infer
   * this itself — an unscoped list looks identical in all three places.
   */
  onOpenContact?: (contactId: string) => void;
}) => {
  const { contact, isLoading: isContactLoading, error: contactError } = useContact(contactId);
  const needsAllContacts = !contactId;

  const { connectorId, searchQuery, sort } = filters;
  // The People view renders no filter selects, so it has no way to change or clear
  // a category — whatever the default happens to be would silently narrow it. Its
  // rows are already scoped server-side to profiled contacts, which is the only
  // filtering it wants. The sort needs no such guard: it only reorders.
  const category = hasProfile ? "all" : filters.category;

  const {
    contacts,
    myContacts,
    isLoading: isContactsLoading,
    error: contactsError,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useContacts({
    enabled: needsAllContacts,
    connectorId,
    // The "With Bio" category is the same question the People view asks, so it
    // reuses that server-side filter rather than guessing from the list row.
    hasProfile: hasProfile || category === "withBio",
    sort,
  });

  const { data: connectorsData } = useQuery({
    queryKey: ["connectors"],
    queryFn: getConnectors,
    enabled: needsAllContacts,
  });
  const allConnectors = connectorsData?.connectors ?? [];
  // Row lookup spans every connector: a contact can belong to a connector that
  // carries no email and so never earns a filter option in the header, and its
  // row should still show where it came from.
  const connectorsById = new Map(allConnectors.map((c) => [c.id, c]));

  // Debounce the search query for the server-side search
  const [debouncedQuery, setDebouncedQuery] = useState(searchQuery);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(searchQuery), 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const {
    contacts: searchResults,
    isLoading: isSearchLoading,
    fetchNextPage: fetchNextSearchPage,
    hasNextPage: hasNextSearchPage,
    isFetchingNextPage: isFetchingNextSearchPage,
    // Search composes with the active filters, not just the connector: "With Bio"
    // restricts the ranked results to profiled contacts server-side, exactly like
    // the browse list above (mine/addressable are applied client-side below).
  } = useContactSearch(debouncedQuery, connectorId, hasProfile || category === "withBio");

  const isSearching = debouncedQuery.trim().length > 0;

  // Infinite scroll: auto-load the next page when the sentinel scrolls into view.
  // Both the browse list and search results paginate now — each backed by its own
  // infinite query — so the observer drives whichever source is currently shown.
  // Browse's "mine" category renders a complete set with nothing left to fetch;
  // search ignores the category filter, so it pages regardless.
  const canPaginate = needsAllContacts && (isSearching || category !== "mine");
  const activeFetchNextPage = isSearching ? fetchNextSearchPage : fetchNextPage;
  const activeHasNextPage = isSearching ? hasNextSearchPage : hasNextPage;
  const activeIsFetchingNextPage = isSearching ? isFetchingNextSearchPage : isFetchingNextPage;
  const sentinelRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel || !canPaginate || !activeHasNextPage) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !activeIsFetchingNextPage) activeFetchNextPage();
      },
      { rootMargin: "200px" },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [canPaginate, activeHasNextPage, activeIsFetchingNextPage, activeFetchNextPage]);

  const { openSidebar } = useRightSidebar();
  const [, setLocation] = useLocation();
  const isDesktop = useMediaQuery("md");

  const [contactToDelete, setContactToDelete] = useState<Contact | null>(null);
  const [contactToMerge, setContactToMerge] = useState<Contact | null>(null);
  const queryClient = useQueryClient();

  const deleteContactMutation = useMutation({
    mutationFn: async (contactId: string) => {
      await api.post(`/api/a/contacts/${contactId}/delete`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: contactQueries.all() });
    },
  });

  const mergeContactsMutation = useMutation({
    mutationFn: async ({
      sourceContactId,
      targetContactId,
    }: {
      sourceContactId: string;
      targetContactId: string;
    }) => {
      await api.post(`/api/a/contacts/${sourceContactId}/merge`, {
        targetContactId,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: contactQueries.all() });
    },
  });

  const handleDeleteClick = (contact: Contact, e: React.MouseEvent) => {
    e.stopPropagation();
    setContactToDelete(contact);
  };

  const handleMergeClick = (contact: Contact, e: React.MouseEvent) => {
    e.stopPropagation();
    setContactToMerge(contact);
  };

  const handleDeleteConfirm = async () => {
    if (!contactToDelete) return;

    try {
      await deleteContactMutation.mutateAsync(contactToDelete.id);
      setContactToDelete(null);
    } catch (error) {
      console.error("Failed to delete contact:", error);
      alert("Failed to delete contact. Please try again.");
    }
  };

  const handleDeleteCancel = () => {
    setContactToDelete(null);
  };

  const handleMerge = async (sourceContactId: string, targetContactId: string) => {
    try {
      await mergeContactsMutation.mutateAsync({
        sourceContactId,
        targetContactId,
      });
      setContactToMerge(null);
    } catch (error) {
      console.error("Failed to merge contacts:", error);
      alert("Failed to merge contacts. Please try again.");
    }
  };

  const handleMergeCancel = () => {
    setContactToMerge(null);
  };

  if (isContactLoading || isContactsLoading) {
    return <Loader />;
  }

  const error = contactError ?? contactsError;
  if (error) {
    return <ErrorMessage error={error} />;
  }

  // Determine which contacts to render based on what's available
  let contactsToRender: Contact[];

  if (isSearching && needsAllContacts) {
    contactsToRender = searchResults;
  } else if (contact) {
    contactsToRender = [contact];
  } else {
    contactsToRender = contacts;
  }

  // Filter out merged contacts
  const filteredContacts = contactsToRender.filter((c) => !c.mergedInto);

  const isBrowsing = needsAllContacts && !isSearching;
  const isSearchingList = needsAllContacts && isSearching;
  // The category filter composes with search: "With Bio" and the connector are
  // applied server-side (see the search hook); the client-side categories (mine,
  // addressable) are applied here.
  //
  // Sorting is server-side: every list arrives in the order its endpoint chose —
  // the browse list in the requested sort, "Me" user-first, search by
  // relevance — and filtering preserves it, so nothing is reordered client-side.
  // (The "Me" category renders the server's complete user/assistant set;
  // the paginated pages only carry whichever of those rows happen to have loaded.)
  // A single-contact list is fixed, so it is neither filtered nor reordered.
  const listedContacts = isSearchingList
    ? filteredContacts.filter((c) => matchesCategory(c, category))
    : isBrowsing
      ? category === "mine"
        ? myContacts
        : filteredContacts.filter((c) => matchesCategory(c, category))
      : filteredContacts;

  const showEmptyState = !isSearchLoading && listedContacts.length === 0;

  const renderContactRow = (contact: Contact) => {
    return (
      <ContactRow
        key={contact.id}
        contact={contact}
        connector={
          contact.connectorId !== undefined ? connectorsById.get(contact.connectorId) : undefined
        }
        onOpen={() => {
          // The settings list drills down to the contact as a child screen
          // (breadcrumb on desktop, nav-stack title on mobile) — a push, so
          // browser back returns to the list.
          if (onOpenContact) {
            onOpenContact(contact.id);
            return;
          }
          // Scoped lists (a thread's participants): mobile has no room for a
          // sidebar, so the detail is a stacked page with the chrome chevron
          // returning here.
          if (!isDesktop) {
            setLocation(`/contacts/${contact.id}`);
            return;
          }
          // `withBack` so the detail can return to whatever list opened it. On
          // the People page no sidebar is open to capture, so no back arrow
          // renders and the close button remains the only exit.
          openSidebar(
            {
              type: "contactList",
              contactId: contact.id,
              defaultTab: "bio" as const,
            },
            { withBack: true },
          );
        }}
        onMerge={handleMergeClick}
        onDelete={handleDeleteClick}
        isDeletePending={deleteContactMutation.isPending}
      />
    );
  };

  return (
    <div className="flex flex-col gap-4">
      {isSearching && isSearchLoading ? (
        <div className="flex justify-center py-8">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </div>
      ) : showEmptyState ? (
        <div className="p-4 text-center text-gray-500">
          {isSearching ? `No people found matching "${debouncedQuery}"` : "No people found."}
        </div>
      ) : (
        <div className={cardListClass}>{listedContacts.map(renderContactRow)}</div>
      )}

      {/* Infinite scroll: the observer above calls the active source's
          fetchNextPage() when this sentinel scrolls into view. Works for both the
          browse list and search results; the "Me" category renders a
          complete set with nothing left to page through (canPaginate gates that,
          matching the observer's guards). */}
      {canPaginate && activeHasNextPage && <div ref={sentinelRef} />}
      {activeIsFetchingNextPage && (
        <div className="flex justify-center py-2">
          <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={contactToDelete !== null}
        onOpenChange={(open) => !open && handleDeleteCancel()}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Contact</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete <strong>{contactToDelete?.name}</strong>? This action
              cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={handleDeleteCancel}
              data-statsig-name="contacts-delete-cancel"
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteConfirm}
              disabled={deleteContactMutation.isPending}
              data-statsig-name="contacts-delete-confirm"
            >
              {deleteContactMutation.isPending ? "Deleting..." : "Delete"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Merge Contact Dialog */}
      <MergeContactDialog
        sourceContact={contactToMerge}
        availableContacts={contacts}
        onMerge={handleMerge}
        onCancel={handleMergeCancel}
        isMerging={mergeContactsMutation.isPending}
      />
    </div>
  );
};
